const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
]);

const cutAtLastUnclosedFence = (raw: string) => {
  let count = 0;
  let idx = -1;
  let start = 0;

  while (true) {
    idx = raw.indexOf("```", start);
    if (idx === -1) break;
    count += 1;
    start = idx + 3;
  }

  if (count % 2 === 1) {
    return raw.slice(0, start - 3);
  }

  return raw;
};

const extractTagName = (tagContent: string) => {
  const trimmed = tagContent.trim();
  if (!trimmed) return "";
  const noSlash = trimmed[0] === "/" ? trimmed.slice(1) : trimmed;
  const match = /^[a-zA-Z0-9:-]+/.exec(noSlash);
  return match ? match[0].toLowerCase() : "";
};

const getOpenTableInfo = (raw: string) => {
  const tagRe = /<\/?table\b[^>]*>/gi;
  let depth = 0;
  let lastOpenIndex = -1;
  let lastOpenTagEnd = -1;
  let match: RegExpExecArray | null;

  while ((match = tagRe.exec(raw))) {
    const tag = match[0];
    const isClosing = tag.startsWith("</");

    if (isClosing) {
      depth = Math.max(0, depth - 1);
      if (depth === 0) {
        lastOpenIndex = -1;
        lastOpenTagEnd = -1;
      }
    } else {
      depth += 1;
      if (depth === 1) {
        lastOpenIndex = match.index;
        lastOpenTagEnd = match.index + tag.length;
      }
    }
  }

  if (depth > 0 && lastOpenIndex >= 0 && lastOpenTagEnd >= 0) {
    return { openIndex: lastOpenIndex, openTagEnd: lastOpenTagEnd };
  }

  return null;
};

const applyHtmlTablePrecompletion = (raw: string) => {
  const tableInfo = getOpenTableInfo(raw);
  if (!tableInfo) return raw;

  const tableBody = raw.slice(tableInfo.openTagEnd);
  const lastTrClose = tableBody.lastIndexOf("</tr>");
  if (lastTrClose === -1) return raw;

  const completedRows = tableBody.slice(0, lastTrClose + 5);
  const prefix = raw.slice(0, tableInfo.openIndex);
  return `${prefix}<table>${completedRows}</table>`;
};

const isMarkdownTableSeparator = (line: string) => {
  return /^\s*\|?(\s*:?-+:?\s*\|)+\s*:?-+:?\s*\|?\s*$/.test(line);
};

const applyMarkdownTableGating = (text: string) => {
  const lines = text.split("\n");
  const hasTrailingNewline = text.endsWith("\n");
  const output: string[] = [];
  let inTable = false;

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const isLast = i === lines.length - 1;

    if (!inTable) {
      if (
        line.includes("|") &&
        i + 1 < lines.length &&
        isMarkdownTableSeparator(lines[i + 1])
      ) {
        output.push(line);
        output.push(lines[i + 1]);
        i += 1;
        inTable = true;
        continue;
      }

      output.push(line);
      continue;
    }

    if (!line.includes("|") || line.trim() === "") {
      inTable = false;
      output.push(line);
      continue;
    }

    if (isLast && !hasTrailingNewline) {
      continue;
    }

    output.push(line);
  }

  return output.join("\n");
};

const getSafeHtmlPrefix = (raw: string) => {
  const stack: string[] = [];
  let lastBalancedIndex = 0;
  let i = 0;

  while (i < raw.length) {
    const lt = raw.indexOf("<", i);
    if (lt === -1) break;

    if (raw.startsWith("<!--", lt)) {
      const end = raw.indexOf("-->", lt + 4);
      if (end === -1) {
        return raw.slice(0, lastBalancedIndex);
      }
      i = end + 3;
      continue;
    }

    if (raw.slice(lt, lt + 9).toLowerCase() === "<!doctype") {
      const end = raw.indexOf(">", lt + 9);
      if (end === -1) {
        return raw.slice(0, lastBalancedIndex);
      }
      i = end + 1;
      continue;
    }

    const gt = raw.indexOf(">", lt + 1);
    if (gt === -1) break;

    const tagContent = raw.slice(lt + 1, gt).trim();
    if (!tagContent) {
      i = gt + 1;
      continue;
    }

    const isClosing = tagContent[0] === "/";
    const isSelfClosing = tagContent.endsWith("/");
    const name = extractTagName(tagContent);

    if (name) {
      if (isClosing) {
        if (stack.length && stack[stack.length - 1] === name) {
          stack.pop();
        }
      } else if (!isSelfClosing && !VOID_TAGS.has(name)) {
        stack.push(name);
      }
    }

    if (stack.length === 0) {
      lastBalancedIndex = gt + 1;
    }

    i = gt + 1;
  }

  return stack.length === 0 ? raw : raw.slice(0, lastBalancedIndex);
};

export const getSafeStreamingText = (raw: string) => {
  if (!raw) return "";

  const fencedSafe = cutAtLastUnclosedFence(raw);
  const htmlTableSafe = applyHtmlTablePrecompletion(fencedSafe);
  const markdownTableSafe = applyMarkdownTableGating(htmlTableSafe);
  return getSafeHtmlPrefix(markdownTableSafe);
};
